/**
 * Opening the campaign form with a segment already chosen.
 *
 * ## WHY THIS IS A MODULE AND NOT FOUR LINES IN `NewCampaignPage`
 *
 * `/campaigns/new?segment=<id>` is what the Overview's "Worth doing next" card
 * links to once a counted cohort has been saved: the button beside *"1,284
 * people have not heard from you in 90 days"* now leads to that number's
 * audience, which is the gap slice 32 recorded and slice 50 closes.
 *
 * `NewCampaignPage` cannot be rendered by a verification suite — it reaches
 * `lib/api/*`, which reaches `lib/config.ts`, which reads `import.meta.env` and
 * evaluates only inside Vite. Everything left inside that file is therefore
 * assertable only as source text, which proves a string is present and nothing
 * about what it does. The two decisions that actually decide whether somebody
 * who just made an audience arrives at a form that has it are here instead,
 * as pure functions over plain data, so a probe can *run* them.
 *
 * ## THE DEFECT THIS EXISTS TO CLOSE, WHICH IS NOT THE OBVIOUS ONE
 *
 * The form lists segments at `MAX_PAGE_SIZE` — 100, sorted by name — and honours
 * a requested id only when it names a row in the list it is actually offering.
 * That rule is right, and it is `?template=`'s rule: an id that is not on offer
 * would be submitted, refused by the server, and reported as a field error on a
 * field the reader never touched.
 *
 * But applied to a *segment* it fails the one person it was built for. On a
 * workspace with more than a hundred segments, a cohort named "Dormant 90 days ·
 * 26 Sep 2026" can sit outside the first page by name, so the prefill silently
 * does nothing and somebody who has just made an audience lands on an empty
 * picker. {@link offeredSegments} is the answer: the requested segment is read
 * on its own and merged into the options, so "on offer" includes the one thing
 * the URL asked for. It is not a way around the rule — the id still has to name
 * a segment this tenant can read, or the extra read answers nothing and the
 * prefill does not take.
 *
 * ## DERIVED, NEVER SEEDED BY AN EFFECT
 *
 * {@link prefilledSegmentId} takes the reader's own choice and whether they have
 * made one, and returns which id the field shows. An effect pushing the
 * requested id into state would need a "have I already seeded this" flag, and
 * that flag is the bug: it makes clearing the field a state the component can
 * re-seed out of. Here the first interaction wins permanently, including a
 * choice of none.
 *
 * Nothing in this file is channel-aware, and nothing should be. A cohort is
 * counted by person (`person_id`, since slice 58), so one audience is reachable on both
 * channels at different sizes, and which of those figures matters is the
 * question `SegmentField`'s per-option description already answers.
 */
import type { Segment, SegmentSummary } from '@wend/shared';

/**
 * What the picker lists a segment by: every fact both reads carry, and no fact
 * either of them had to be asked twice for.
 *
 * `SegmentSummary` minus `datasetCount`, because that one number is the only
 * thing the list read has and the detail read does not — and **it is derived on
 * the server, so it may not be derived again here.**
 *
 * The first version of this function answered `datasets.length`, which looks
 * like reading a fact off the read and is not. The server computes
 * `datasetCount` from the membership relation, and the two disagree on a real
 * segment: a static segment holding one upload that imported nothing has one
 * `segment_datasets` row, so `datasets.length` is `1`, while a count taken
 * across the join to `contacts` is whatever that join decides — and the join
 * itself is being changed. Two implementations of one rule is this repo's worst
 * defect shape, and a screen that showed `1 list` beside the server's `none`
 * would be the two arguing in public.
 *
 * There is no third option available here: the detail read carries no
 * `datasetCount` at all. So the number is not invented — it is dropped, and the
 * picker asks for nothing it does not draw. What it draws is the name and the
 * per-channel reach, from `counts`.
 *
 * `datasets` is dropped rather than the fields being named one by one so that a
 * fact added to both shapes — a cohort's provenance is the current one — travels
 * without an edit here.
 */
export type PickableSegment = Omit<SegmentSummary, 'datasetCount'>;

export function asPickableSegment(segment: Segment): PickableSegment {
  // Slice 67: a rule segment's tree and refresh time are detail-only facts the
  // list row does not carry either, so they go with `datasets`.
  const { datasets, rules, rulesRefreshedAt, ...rest } = segment;
  return rest;
}

/**
 * The segments the picker offers: the page the list read returned, plus the one
 * the URL asked for when that page does not already carry it.
 *
 * Inserted in name order rather than appended, so the option a person is looking
 * for is where the rest of the list would put it — but the listed rows are never
 * re-sorted, because they arrived in the server's collation and re-ordering them
 * here would make the field's "the first N by name are listed" line describe a
 * different N.
 *
 * `null` for the requested segment covers three cases that need no distinction:
 * no `?segment=` in the URL, a read still in flight, and a read that came back
 * with nothing because the id names a segment that has been deleted. In all
 * three the options are the listed page and the prefill does not take.
 */
export function offeredSegments(
  listed: readonly PickableSegment[],
  requested: PickableSegment | null,
): readonly PickableSegment[] {
  if (requested === null) return listed;
  if (listed.some((row) => row.id === requested.id)) return listed;

  const at = listed.findIndex((row) => row.name.localeCompare(requested.name, 'en-GB') > 0);
  if (at < 0) return [...listed, requested];
  return [...listed.slice(0, at), requested, ...listed.slice(at)];
}

/**
 * Which segment id the field shows.
 *
 * The request is honoured only until the reader touches the field, and only when
 * it names something on offer. After that their choice is the answer, including
 * `null` for "none".
 */
export function prefilledSegmentId({
  requested,
  touched,
  chosen,
  offered,
}: {
  /** The `?segment=` value, or null when the URL carried none. */
  requested: string | null;
  /** True once the reader has picked anything, including nothing. */
  touched: boolean;
  /** What the reader picked. */
  chosen: string | null;
  /** The rows the picker is actually offering right now. */
  offered: readonly { readonly id: string }[];
}): string | null {
  const honoured =
    !touched && requested !== null && offered.some((row) => row.id === requested);
  return honoured ? requested : chosen;
}
